import { useEffect, useLayoutEffect, useRef } from 'react';

/*
  "An automatic reload would destroy something here."

  Deliberately NOT the unsaved-changes registry (src/guards/unsavedChanges.ts), which
  answers a different question: *the client has typed something they would lose*. That
  one drives a "leave site?" prompt, so anything registered with it is claiming there
  are edits worth warning about. The two states this latch exists for have no edits at
  all — an in-flight submission, and a confirmation screen the client is reading — and
  registering them as "unsaved changes" would eventually surface a nonsense prompt on
  a page where the work is already safely sent.

  Only the PWA updater reads this, and only for the detection-time apply on the intake
  form (updatePolicy.ts, #721). Everywhere else a waiting worker still waits for a
  navigation, and a navigation can't fire under a client who isn't navigating, so
  nothing else needs to declare anything.

  Lives in `src/pwa/` rather than beside its one caller in `src/intake/` on purpose:
  pwaUpdate is in the entry chunk and the intake wizard is lazily loaded, so a
  `pwa -> intake` import would pull the whole wizard into every user's first paint.
*/

const blockers = new Set<string>();

/** Would an automatic reload right now destroy something the client can see? */
export function isAutoReloadBlocked(): boolean {
  return blockers.size > 0;
}

/** Test-only: reset module state between cases. */
export function __resetAutoReloadBlockers(): void {
  blockers.clear();
}

let nextId = 0;

/**
 * Hold the latch while `blocked` is true. Clears on unmount, so a component that
 * disappears mid-block never leaves the latch stuck closed — which would silently
 * turn the detection-time apply back off for the rest of the page's life.
 */
export function useBlockAutoReload(blocked: boolean): void {
  const idRef = useRef<string | undefined>(undefined);
  if (idRef.current === undefined) idRef.current = `ar-${nextId++}`;

  // A LAYOUT effect, so the latch changes in the same synchronous commit that puts the
  // new state on screen. A passive `useEffect` runs after that commit (and, for an update
  // set from an async continuation, after paint), which left a window in which the page
  // showed its settled state while the latch was still held (#2112; seen as a flaky
  // assertion in a page test since removed). The mutation is a module-level Set write, so
  // there is nothing here that needs to wait for paint.
  useLayoutEffect(() => {
    const id = idRef.current!;
    if (blocked) blockers.add(id);
    else blockers.delete(id);
  }, [blocked]);

  useEffect(() => {
    const id = idRef.current!;
    return () => {
      blockers.delete(id);
    };
  }, []);
}
